/**
 * STANDARD-MOTOR til sitets vandrette slidere (genanvendeligt modul).
 *
 * Finder automatisk alle [data-hscroll]-containere på siden (og også
 * [data-hscroll-leaf]) og kobler dem til deres blad-indikator
 * (snippets/scroll-progress-bar.liquid, som skal stå lige efter containeren) og
 * eventuelle pile (snippets/scroll-arrows.liquid).
 *
 * Opskrift til en ny slider i en sektion:
 *   <div class="hscroll-arrows-wrapper">           (giver pile + kantgradienter)
 *     {% render 'scroll-arrows' %}
 *     <div class="hscroll min-egen-klasse" data-hscroll> ...kort... </div>
 *     {% render 'scroll-progress-bar', track_color: section.settings.accent_color %}
 *   </div>
 *   (Scriptet indlæses ÉN gang globalt i layout/theme.liquid - tilføj IKKE en egen <script>-tag i sektionen.)
 *
 * Den brede scrollbar-pille findes som "klassisk" version (assets/
 * horizontal-scroll-classic.js + snippets/scroll-progress-bar-classic.liquid).
 *
 * Detaljer:
 *  - Pilene hopper kort for kort (scrollByPage), så intet kort springes over.
 *  - Scrollbaren er sitets logo-blad på en tynd linje (se snippets/scroll-progress-bar.liquid).
 *    Bladet har fast størrelse og bevæger sig fra sporets venstre til højre side
 *    proportionalt med scroll-positionen.
 *  - Bladets hvilepositioner ligger START_INSET_RATIO (20%) af sporets bredde inde fra
 *    begge ender, så indrykningen skalerer med sektionens bredde.
 *  - Linjen er to uafhængige stykker (".hscroll-leaf-line--start"/"--end"), hvis
 *    "left"/"width" beregnes i updateThumb ud fra bladets position, med et fast
 *    mellemrum (LEAF_GAP) til bladets kanter.
 *  - Bladet er et håndtag: man kan trække i det (mus og touch), og klikke/trække i linjen.
 *    Bladet følger markøren direkte, mens indholdet glider blødt efter (easing).
 *  - Klik-og-træk direkte i selve kort-/billedrækken (mus).
 */
const initHorizontalScroll = () => {
  const START_INSET_RATIO = 0.2; // 20% af sporets bredde - samme ratio som det klassiske håndtag (se horizontal-scroll-classic.js)
  const LEAF_GAP = 14; // px - luft mellem bladets kanter og hvert linjestykke, se updateThumb

  document.querySelectorAll('[data-hscroll], [data-hscroll-leaf]').forEach((track) => {
    if (track.dataset.hscrollInit) return; // undgå dobbelt-initialisering
    track.dataset.hscrollInit = 'true';

    const bar = track.nextElementSibling;
    if (!bar || !bar.classList.contains('hscroll-leaf-bar')) return;

    const barTrack = bar.querySelector('.hscroll-leaf-track');
    const thumb = bar.querySelector('.hscroll-leaf-thumb');
    const lineStart = bar.querySelector('.hscroll-leaf-line--start');
    const lineEnd = bar.querySelector('.hscroll-leaf-line--end');
    if (!barTrack || !thumb) return;

    // Valgfrie pile-knapper (se snippets/scroll-arrows.liquid).
    const arrowsWrapper = track.closest('.hscroll-arrows-wrapper');
    const prevArrow = arrowsWrapper ? arrowsWrapper.querySelector('.hscroll-arrow--prev') : null;
    const nextArrow = arrowsWrapper ? arrowsWrapper.querySelector('.hscroll-arrow--next') : null;

    function updateArrows(maxScroll) {
      if (!prevArrow && !nextArrow) return;
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= maxScroll - 1;
      if (prevArrow) prevArrow.classList.toggle('is-at-limit', atStart);
      if (nextArrow) nextArrow.classList.toggle('is-at-limit', maxScroll <= 1 || atEnd);
    }

    // Pile-klik: hop "kort for kort" i stedet for en fast procentdel af bredden, så et kort
    // aldrig skubbes halvt ud af billedet, før man har nået at læse det. Reglerne:
    //  - Næste: det første kort, der ikke er helt synligt i højre side, bliver det nye
    //    første kort i venstre side (intet kort springes over).
    //  - Forrige: spejlvendt - det første kort, der stadig lader det kort stå helt synligt
    //    i højre side, som lige stod yderst til venstre.
    // Alle mål er kortenes egne snap-punkter (scroll-snap-align: start), så scroll-snap
    // ikke flytter dem bagefter.
    function scrollByPage(direction) {
      const trackLeft = track.getBoundingClientRect().left;
      const items = Array.from(track.children).map((child) => {
        const rect = child.getBoundingClientRect();
        const left = rect.left - trackLeft + track.scrollLeft;
        return { left: left, right: left + rect.width };
      });
      const viewLeft = track.scrollLeft;
      const viewRight = viewLeft + track.clientWidth;
      const maxScroll = track.scrollWidth - track.clientWidth;
      let target;

      if (direction > 0) {
        const firstCut = items.find((item) => item.right > viewRight + 1);
        target = firstCut ? firstCut.left : maxScroll;
        // Hopper vi ikke længere end et kort frem (kort bredere end vinduet), så gå i det
        // mindste et lille stykke frem.
        if (target <= viewLeft + 1) target = viewLeft + track.clientWidth * 0.9;
      } else {
        const firstFull = items.find((item) => item.left >= viewLeft - 1);
        const anchor = firstFull ? firstFull.left : viewLeft;
        const wanted = anchor - track.clientWidth;
        const candidate = items.find((item) => item.left >= wanted - 1);
        target = candidate ? candidate.left : 0;
        if (target >= viewLeft - 1) target = viewLeft - track.clientWidth * 0.9;
      }

      track.scrollTo({ left: Math.min(Math.max(target, 0), maxScroll), behavior: 'smooth' });
    }

    if (prevArrow) prevArrow.addEventListener('click', () => scrollByPage(-1));
    if (nextArrow) nextArrow.addEventListener('click', () => scrollByPage(1));

    // Træk-tilstand (se "Klik-og-træk i bladet" længere nede). Erklæres her, fordi
    // updateThumb bruger dem.
    let pointerDown = false; // finger/mus er nede på bladet/linjen
    let easing = false; // indholdet glider stadig frem mod det ønskede punkt
    let dragRatio = 0; // ønsket scroll-position (0-1) ud fra markøren
    let rafId = null;

    // Bladets geometri. Indrykningen i begge ender er 20% af sporets egen bredde
    // (START_INSET_RATIO, samme ratio som det klassiske håndtag). Det giver et synligt
    // mellemrum mellem bladet og linjens yderpunkter, der skalerer med sektionens bredde.
    // Bruges både til at placere bladet (updateThumb) og til at oversætte musens position
    // tilbage til en scroll-position (scrollFromPointerX), så de to altid passer sammen.
    function getGeometry() {
      const barTrackWidth = barTrack.clientWidth;
      const thumbWidth = thumb.offsetWidth || 24;
      const edgeGap = barTrackWidth * START_INSET_RATIO;
      const maxThumbLeft = Math.max(barTrackWidth - thumbWidth - (edgeGap * 2), 0);
      return { barTrackWidth, thumbWidth, edgeGap, maxThumbLeft };
    }

    // På mobil og små tablets (op til 768px) skal linjen fylde hele skærmbredden, også selvom
    // sektionen har luft i siderne (fx 20px til venstre for kortrækken). Baren trækkes derfor ud
    // til skærmens venstre og højre kant. På større skærme står den som sektionen selv bestemmer.
    function stretchBar() {
      bar.style.marginLeft = '';
      bar.style.marginRight = '';
      bar.style.paddingLeft = '';
      bar.style.paddingRight = '';
      if (window.innerWidth > 768) return;
      const vw = document.documentElement.clientWidth;
      const rect = bar.getBoundingClientRect();
      if (!rect.width) return;
      bar.style.paddingLeft = '0px';
      bar.style.paddingRight = '0px';
      bar.style.marginLeft = (-rect.left) + 'px';
      bar.style.marginRight = (-(vw - rect.right)) + 'px';
    }

    function updateThumb() {
      if (!track.scrollWidth) return;
      stretchBar();

      const maxScroll = track.scrollWidth - track.clientWidth;
      // Mens bladet trækkes (og indholdet "glider efter"), placeres bladet direkte ud fra
      // markøren (dragRatio) i stedet for ud fra den halvvejs ankomne scroll-position -
      // så bladet føles direkte, og indholdet ligger blødt efter.
      const ratio = (pointerDown || easing)
        ? dragRatio
        : (maxScroll > 0 ? track.scrollLeft / maxScroll : 0);
      updateArrows(maxScroll);

      const { barTrackWidth, thumbWidth, edgeGap, maxThumbLeft } = getGeometry();

      const thumbLeft = edgeGap + ratio * maxThumbLeft;
      thumb.style.left = thumbLeft + 'px';

      // De to linjestykker ("hscroll-leaf-line--start/--end") får "left"/"width" beregnet
      // ud fra bladets aktuelle position, med et fast mellemrum (LEAF_GAP) til bladets kanter
      // i begge sider, så mellemrummet følger bladet rundt.
      if (lineStart) {
        const startWidth = Math.max(thumbLeft - LEAF_GAP, 0);
        lineStart.style.left = '0px';
        lineStart.style.width = startWidth + 'px';
      }
      if (lineEnd) {
        const endLeft = thumbLeft + thumbWidth + LEAF_GAP;
        const endWidth = Math.max(barTrackWidth - endLeft, 0);
        lineEnd.style.left = endLeft + 'px';
        lineEnd.style.width = endWidth + 'px';
      }

      // skjul baren helt, hvis der reelt ikke er noget at scrolle
      bar.style.display = maxScroll <= 1 ? 'none' : '';
    }

    track.addEventListener('scroll', updateThumb, { passive: true });
    window.addEventListener('resize', updateThumb);
    updateThumb();

    window.addEventListener('load', updateThumb);
    setTimeout(updateThumb, 300);

    // Klik-og-træk i bladet (håndtaget) og i selve linjen -> scroll containeren.
    // Bladets MIDTE følger markøren: pointerToRatio oversætter markørens x-position til en
    // scroll-ratio via samme geometri som updateThumb (inkl. 20%-indrykningen i begge
    // ender), så bladet ligger præcis under markøren. Griber man fat i selve bladet, huskes
    // afstanden fra bladets midte til markøren (grabOffset), så bladet ikke hopper.
    //
    // Blødhed: bladet placeres direkte ud fra markøren, mens selve indholdet "glider efter"
    // med en blød easing (ease-loopet nedenfor, ca. 22% af afstanden pr. billede) i stedet
    // for at hoppe i trin ved hvert musebevægelse. Slipper man, glider indholdet færdigt, og
    // først derefter genindsættes scroll-snap. "Reducer bevægelse" i systemet slår easingen
    // fra.
    let grabOffset = 0;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function pointerToRatio(clientX) {
      const rect = barTrack.getBoundingClientRect();
      const { thumbWidth, edgeGap, maxThumbLeft } = getGeometry();
      if (maxThumbLeft <= 0) return 0;
      const thumbLeft = clientX - rect.left - thumbWidth / 2;
      return Math.min(Math.max((thumbLeft - edgeGap) / maxThumbLeft, 0), 1);
    }

    function stopEasing() {
      easing = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
      thumb.style.transition = '';
      bar.classList.remove('is-dragging');
      updateThumb();
    }

    function easeStep() {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const target = dragRatio * maxScroll;
      const diff = target - track.scrollLeft;
      if (reduceMotion || Math.abs(diff) < 0.75) {
        track.scrollLeft = target;
        if (!pointerDown) { stopEasing(); return; }
      } else {
        let step = diff * 0.22;
        // Minimum 1px pr. billede, så easingen ikke går i stå på afrundede scroll-værdier.
        if (Math.abs(step) < 1) step = Math.sign(diff);
        track.scrollLeft += step;
      }
      updateThumb();
      rafId = requestAnimationFrame(easeStep);
    }

    barTrack.addEventListener('pointerdown', (event) => {
      pointerDown = true;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      thumb.style.transition = 'none';
      bar.classList.add('is-dragging');
      barTrack.setPointerCapture(event.pointerId);

      grabOffset = 0;
      if (thumb.contains(event.target)) {
        const thumbRect = thumb.getBoundingClientRect();
        grabOffset = event.clientX - (thumbRect.left + thumbRect.width / 2);
      }
      dragRatio = pointerToRatio(event.clientX - grabOffset);
      easing = true;
      if (!rafId) rafId = requestAnimationFrame(easeStep);
    });

    barTrack.addEventListener('pointermove', (event) => {
      if (pointerDown) dragRatio = pointerToRatio(event.clientX - grabOffset);
    });

    ['pointerup', 'pointercancel'].forEach((eventName) => {
      barTrack.addEventListener(eventName, () => {
        pointerDown = false; // easeStep afslutter og genindsætter snap, når indholdet er fremme
      });
    });

    // Klik-og-træk direkte i selve kort-/billedrækken (samme opskrift som
    // horizontal-scroll-classic.js).
    let trackDragging = false;
    let trackDragMoved = false;
    let trackStartX = 0;
    let trackStartScrollLeft = 0;

    track.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      trackDragging = true;
      trackDragMoved = false;
      trackStartX = event.clientX;
      trackStartScrollLeft = track.scrollLeft;
      event.preventDefault();
    });

    track.addEventListener('pointermove', (event) => {
      if (!trackDragging) return;
      const deltaX = event.clientX - trackStartX;
      if (!trackDragMoved && Math.abs(deltaX) < 6) return;
      if (!trackDragMoved) {
        trackDragMoved = true;
        track.style.scrollBehavior = 'auto';
        track.style.scrollSnapType = 'none';
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      track.scrollLeft = trackStartScrollLeft - deltaX;
    });

    function endTrackDrag() {
      if (!trackDragging) return;
      trackDragging = false;
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
      track.classList.remove('is-dragging');
      if (trackDragMoved) {
        const suppressClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        document.addEventListener('click', suppressClick, { capture: true, once: true });
        setTimeout(() => {
          document.removeEventListener('click', suppressClick, { capture: true });
        }, 0);
      }
      trackDragMoved = false;
    }

    ['pointerup', 'pointercancel'].forEach((eventName) => {
      track.addEventListener(eventName, endTrackDrag);
    });
  });
};

// Scriptet indlæses nu én gang i theme.liquid (defer). Initialiseringen kører straks, hvis siden allerede er
// parset, ellers ved DOMContentLoaded - og igen når en sektion genindlæses i temaeditoren.
// Hver slider initialiseres kun én gang (data-hscroll-init), så gentagne kald er ufarlige.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHorizontalScroll);
} else {
  initHorizontalScroll();
}
document.addEventListener('shopify:section:load', initHorizontalScroll);
